import { Loading } from "../../app/Status";
import { useState } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  ALLOCATION_LABELS,
  ALLOCATIONS,
  BUYOUT_MATERIAL_LABELS,
  BUYOUT_MATERIALS,
  SERVICE_HOURS,
  SERVICE_HOURS_LABELS,
  CONTRACT_BILLING_LABELS,
  CONTRACT_BILLINGS,
  CONTRACT_LINE_KIND_LABELS,
  CONTRACT_LINE_KINDS,
  OBLIGATION_KIND_LABELS,
  PRICE_FORM_LABELS,
  PRICE_FORMS,
  PRICE_PERIOD_LABELS,
  PRICE_PERIODS,
  PRICE_REASON_LABELS,
  PRICE_REASONS,
  type Allocation,
  type BuildingDto,
  type BuyoutMaterial,
  type ContractBilling,
  type ContractDetailDto,
  type ContractLineDto,
  type ContractLineKind,
  type ObligationKind,
  type PriceForm,
  type PricePeriod,
  type PriceReason,
  type ServiceHours,
  type UncoveredObligationDto,
} from "@klantpunt/shared";
import { Meta } from "../../app/Meta";
import { Modal } from "../../app/Modal";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { buildingKey } from "../building/useBuilding";
import { euroInput } from "../mjop/MjopFields";
import { parseEuro } from "../../pages/Platform";
import { errMsg, fmtDate, intervalText, useContractsRefresh, useUncovered } from "./useContracts";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
const money = (v: string) => (v.trim() ? parseEuro(v) : null);
const bad = (v: string) => v.trim() !== "" && parseEuro(v) === null;

/** Bedragvelden van een prijs: aangenomen één bedrag, nacalculatie tarieven en een raming per jaar. */
function PriceFields({
  billing,
  priceForm,
  period,
  v,
  set,
}: {
  billing: ContractBilling;
  priceForm: PriceForm;
  period: PricePeriod;
  v: PriceState;
  set: (p: Partial<PriceState>) => void;
}) {
  if (billing === "fixed")
    return (
      <label className="field">
        <span>Bedrag excl. btw {priceForm === "per_visit" ? "per stuk per keer" : PRICE_PERIOD_LABELS[period]}</span>
        <input inputMode="decimal" value={v.amount} onChange={(e) => set({ amount: e.target.value })} placeholder="€" />
        {bad(v.amount) && <span className="hint err">Vul een bedrag in, bijv. 4.800,00</span>}
      </label>
    );
  return (
    <>
      <div className="grid2">
        <label className="field">
          <span>Uurtarief</span>
          <input inputMode="decimal" value={v.hourly} onChange={(e) => set({ hourly: e.target.value })} />
        </label>
        <label className="field">
          <span>Voorrijkosten</span>
          <input inputMode="decimal" value={v.callout} onChange={(e) => set({ callout: e.target.value })} />
        </label>
      </div>
      <div className="grid2">
        <label className="field">
          <span>Opslag materiaal (%)</span>
          <input inputMode="decimal" value={v.markup} onChange={(e) => set({ markup: e.target.value })} />
        </label>
        <label className="field">
          <span>Raming per jaar</span>
          <input inputMode="decimal" value={v.estimate} onChange={(e) => set({ estimate: e.target.value })} />
        </label>
      </div>
      <p className="hint">Tarieven zijn optioneel. De raming per jaar telt mee in het totaal van het contract.</p>
    </>
  );
}

interface PriceState {
  amount: string;
  hourly: string;
  callout: string;
  markup: string;
  estimate: string;
}
const emptyPrice: PriceState = { amount: "", hourly: "", callout: "", markup: "", estimate: "" };
const priceBody = (billing: ContractBilling, v: PriceState) =>
  billing === "fixed"
    ? { amountCents: money(v.amount) }
    : {
        hourlyRateCents: money(v.hourly),
        calloutCents: money(v.callout),
        materialMarkupPercent: v.markup.trim() ? Number(v.markup.replace(",", ".")) : null,
        estimateYearCents: money(v.estimate),
      };
const priceInvalid = (billing: ContractBilling, v: PriceState) =>
  billing === "fixed"
    ? bad(v.amount)
    : bad(v.hourly) ||
      bad(v.callout) ||
      bad(v.estimate) ||
      (v.markup.trim() !== "" && Number.isNaN(Number(v.markup.replace(",", "."))));

/** Nieuwe post (met startprijs) of een post bewerken. */
export function LineDialog({
  contract: c,
  line: l,
  onClose,
}: {
  contract: ContractDetailDto;
  line?: ContractLineDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useContractsRefresh();
  const [description, setDescription] = useState(l?.description ?? "");
  const [kind, setKind] = useState<ContractLineKind>(l?.kind ?? "preventive");
  const [billing, setBilling] = useState<ContractBilling>(l?.billing ?? "fixed");
  const [priceForm, setPriceForm] = useState<PriceForm>(l?.priceForm ?? "period");
  const [period, setPeriod] = useState<PricePeriod>(l?.period ?? "year");
  const [allocation, setAllocation] = useState<Allocation>(l?.allocation ?? "weighted");
  const [buildingId, setBuildingId] = useState(l?.building?.id ?? "");
  const [notes, setNotes] = useState(l?.notes ?? "");
  const [price, setPrice] = useState<PriceState>(emptyPrice);
  const [includesLabour, setIncludesLabour] = useState(l?.includesLabour ?? true);
  const [includesCallout, setIncludesCallout] = useState(l?.includesCallout ?? true);
  const [materialMode, setMaterialMode] = useState<BuyoutMaterial>(l?.materialMode ?? "none");
  const [materialLimit, setMaterialLimit] = useState(euroInput(l?.materialLimitCents));
  const [maxCalls, setMaxCalls] = useState(l?.maxCallsPerYear != null ? String(l.maxCallsPerYear) : "");
  const [serviceHours, setServiceHours] = useState<ServiceHours>(l?.serviceHours ?? "office");
  const [responseHours, setResponseHours] = useState(l?.responseHours != null ? String(l.responseHours) : "");
  const [busy, setBusy] = useState(false);
  const wholeNumber = (v: string) => v.trim() === "" || /^[1-9]\d{0,3}$/.test(v.trim());
  const breakdown = kind === "breakdown";
  const invalid =
    !description.trim() ||
    (!l && priceInvalid(billing, price)) ||
    (breakdown && (!wholeNumber(maxCalls) || !wholeNumber(responseHours) || bad(materialLimit)));

  async function save() {
    setBusy(true);
    const body = {
      description,
      kind,
      billing,
      priceForm: billing === "fixed" ? priceForm : null,
      period: billing === "fixed" && priceForm === "period" ? period : null,
      allocation,
      buildingId: buildingId || null,
      notes: notes || null,
      ...(breakdown
        ? {
            includesLabour,
            includesCallout,
            materialMode,
            materialLimitCents: materialMode === "limit" ? money(materialLimit) : null,
            maxCallsPerYear: billing === "fixed" && maxCalls.trim() ? Number(maxCalls) : null,
            serviceHours,
            responseHours: responseHours.trim() ? Number(responseHours) : null,
          }
        : {}),
    };
    try {
      const p = priceBody(billing, price);
      const hasPrice = Object.values(p).some((x) => x != null);
      const d = l
        ? await api<ContractDetailDto>(`/contract-lines/${l.id}`, { method: "PATCH", body })
        : await api<ContractDetailDto>(`/contracts/${c.id}/lines`, {
            method: "POST",
            body: {
              id: crypto.randomUUID(),
              ...body,
              price: hasPrice ? { id: crypto.randomUUID(), reason: "start", ...p } : null,
            },
          });
      await refresh(d);
      toast(l ? "Post opgeslagen" : "Post toegevoegd");
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={l ? "Post bewerken" : "Nieuwe post"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || invalid}>
            {l ? "Opslaan" : "Toevoegen"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Omschrijving *</span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="bijv. Preventief onderhoud 4x per jaar"
        />
      </label>
      <div className="grid2">
        <label className="field">
          <span>Soort</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as ContractLineKind)}>
            {CONTRACT_LINE_KINDS.map((k) => (
              <option key={k} value={k}>
                {CONTRACT_LINE_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Afrekenmodel</span>
          <select value={billing} onChange={(e) => setBilling(e.target.value as ContractBilling)}>
            {CONTRACT_BILLINGS.map((k) => (
              <option key={k} value={k}>
                {CONTRACT_BILLING_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {billing === "fixed" && (
        <div className="grid2">
          <label className="field">
            <span>Prijsvorm</span>
            <select value={priceForm} onChange={(e) => setPriceForm(e.target.value as PriceForm)}>
              {PRICE_FORMS.map((k) => (
                <option key={k} value={k}>
                  {PRICE_FORM_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          {priceForm === "period" && (
            <label className="field">
              <span>Periode</span>
              <select value={period} onChange={(e) => setPeriod(e.target.value as PricePeriod)}>
                {PRICE_PERIODS.map((k) => (
                  <option key={k} value={k}>
                    {PRICE_PERIOD_LABELS[k].replace("per ", "Per ")}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
      {billing === "fixed" && priceForm === "period" && !breakdown && (
        <label className="field">
          <span>Prijs per keer terugrekenen</span>
          <select value={allocation} onChange={(e) => setAllocation(e.target.value as Allocation)}>
            {ALLOCATIONS.map((k) => (
              <option key={k} value={k}>
                {ALLOCATION_LABELS[k]}
              </option>
            ))}
          </select>
          <span className="hint">Een indicatie per uitvoering; het vaste bedrag blijft het totaal.</span>
        </label>
      )}
      {billing === "fixed" && priceForm === "per_visit" && (
        <p className="hint">De prijs wordt de kosten per keer van elke uitvoering onder deze post.</p>
      )}
      {c.buildings.length > 1 && (
        <label className="field">
          <span>Gebouw</span>
          <select value={buildingId} onChange={(e) => setBuildingId(e.target.value)}>
            <option value="">Alle gebouwen van het contract</option>
            {c.buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} · {b.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {!l && (
        <PriceFields
          billing={billing}
          priceForm={priceForm}
          period={period}
          v={price}
          set={(p) => setPrice((x) => ({ ...x, ...p }))}
        />
      )}
      {breakdown && (
        <fieldset className="plain">
          <legend className="label">{billing === "fixed" ? "Afkoop storingen" : "Service bij storingen"}</legend>
          {billing === "fixed" && (
            <>
              <div>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={includesLabour}
                    onChange={(e) => setIncludesLabour(e.target.checked)}
                  />
                  <span>Arbeid inbegrepen</span>
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={includesCallout}
                    onChange={(e) => setIncludesCallout(e.target.checked)}
                  />
                  <span>Voorrijden inbegrepen</span>
                </label>
              </div>
              <div className="grid2">
                <label className="field">
                  <span>Materiaal</span>
                  <select value={materialMode} onChange={(e) => setMaterialMode(e.target.value as BuyoutMaterial)}>
                    {BUYOUT_MATERIALS.map((k) => (
                      <option key={k} value={k}>
                        {BUYOUT_MATERIAL_LABELS[k]}
                      </option>
                    ))}
                  </select>
                </label>
                {materialMode === "limit" && (
                  <label className="field">
                    <span>Tot per storing</span>
                    <input
                      inputMode="decimal"
                      value={materialLimit}
                      onChange={(e) => setMaterialLimit(e.target.value)}
                      placeholder="€"
                    />
                    {bad(materialLimit) && <span className="hint err">Vul een bedrag in, bijv. 50,00</span>}
                  </label>
                )}
              </div>
              <label className="field">
                <span>Maximaal aantal storingen per jaar</span>
                <input
                  inputMode="numeric"
                  value={maxCalls}
                  onChange={(e) => setMaxCalls(e.target.value)}
                  placeholder="Leeg = onbeperkt"
                />
                {!wholeNumber(maxCalls) && <span className="hint err">Vul een heel getal in</span>}
              </label>
            </>
          )}
          <div className="grid2">
            <label className="field">
              <span>Bereikbaar</span>
              <select value={serviceHours} onChange={(e) => setServiceHours(e.target.value as ServiceHours)}>
                {SERVICE_HOURS.map((k) => (
                  <option key={k} value={k}>
                    {SERVICE_HOURS_LABELS[k]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Responstijd in uren</span>
              <input
                inputMode="numeric"
                value={responseHours}
                onChange={(e) => setResponseHours(e.target.value)}
                placeholder="bijv. 4"
              />
              {!wholeNumber(responseHours) && <span className="hint err">Vul een heel getal in</span>}
            </label>
          </div>
          <p className="hint">Welke assets eronder vallen, kies je daarna op het tabblad Dekking.</p>
        </fieldset>
      )}
      <label className="field">
        <span>Wat valt eronder</span>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="bijv. klein materiaal tot € 50 inbegrepen, geen vandalisme"
        />
      </label>
    </Modal>
  );
}

/** Nieuwe prijs met ingangsdatum en reden; de oude blijft in de historie. */
export function PriceDialog({ line: l, onClose }: { line: ContractLineDto; onClose: () => void }) {
  const toast = useToast();
  const refresh = useContractsRefresh();
  const cur = l.price;
  const [v, setV] = useState<PriceState>({
    amount: euroInput(cur?.amountCents),
    hourly: euroInput(cur?.hourlyRateCents),
    callout: euroInput(cur?.calloutCents),
    markup: cur?.materialMarkupPercent != null ? String(cur.materialMarkupPercent).replace(".", ",") : "",
    estimate: euroInput(cur?.estimateYearCents),
  });
  const [validFrom, setValidFrom] = useState(today());
  const [reason, setReason] = useState<PriceReason>(l.prices.length ? "indexation" : "start");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const clash = l.prices.some((p) => p.validFrom === validFrom);

  async function save() {
    setBusy(true);
    try {
      const d = await api<ContractDetailDto>(`/contract-lines/${l.id}/prices`, {
        method: "POST",
        body: { id: crypto.randomUUID(), validFrom, reason, note: note || null, ...priceBody(l.billing, v) },
      });
      await refresh(d);
      toast("Prijs vastgelegd");
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Nieuwe prijs"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || !validFrom || clash || priceInvalid(l.billing, v)}
          >
            Vastleggen
          </button>
        </>
      }
    >
      <p className="muted small">{l.description}</p>
      <div className="grid2">
        <label className="field">
          <span>Geldig vanaf *</span>
          <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          {clash && <span className="hint err">Er staat al een prijs met deze datum</span>}
        </label>
        <label className="field">
          <span>Reden</span>
          <select value={reason} onChange={(e) => setReason(e.target.value as PriceReason)}>
            {PRICE_REASONS.map((k) => (
              <option key={k} value={k}>
                {PRICE_REASON_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <PriceFields
        billing={l.billing}
        priceForm={l.priceForm ?? "period"}
        period={l.period ?? "year"}
        v={v}
        set={(p) => setV((x) => ({ ...x, ...p }))}
      />
      <label className="field">
        <span>Toelichting</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="bijv. CBS-index 2027" />
      </label>
    </Modal>
  );
}

/** Uitvoeringen kiezen die onder een post komen (uit de gebouwen van het contract, zonder lopend contract). */
export function CoverDialog({
  contract: c,
  line: initial,
  onClose,
}: {
  contract: ContractDetailDto;
  line?: ContractLineDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useContractsRefresh();
  const [lineId, setLineId] = useState(initial?.id ?? c.lines[0]?.id ?? "");
  const line = c.lines.find((l) => l.id === lineId);
  const cand = useUncovered({ contract: c.id });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const norm = q.trim().toLocaleLowerCase("nl");
  const rows = (cand.data ?? [])
    .filter((r) => !line?.building || r.building.id === line.building.id)
    .filter(
      (r) =>
        !norm ||
        `${r.name} ${r.asset?.code ?? ""} ${r.asset?.name ?? ""} ${r.building.code}`
          .toLocaleLowerCase("nl")
          .includes(norm),
    );

  async function save() {
    setBusy(true);
    try {
      const d = await api<ContractDetailDto>(`/contract-lines/${lineId}/obligations`, {
        method: "POST",
        body: { add: [...picked] },
      });
      await refresh(d);
      toast(`${picked.size === 1 ? "1 uitvoering" : `${picked.size} uitvoeringen`} onder het contract gebracht`);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Uitvoeringen toevoegen"
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || !picked.size || !lineId}
          >
            {picked.size ? `${picked.size} toevoegen` : "Toevoegen"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Post</span>
        <select value={lineId} onChange={(e) => setLineId(e.target.value)}>
          {c.lines.map((l) => (
            <option key={l.id} value={l.id}>
              {l.description}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Zoeken</span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Uitvoering of asset" />
      </label>
      <ObligationChecklist rows={rows} picked={picked} setPicked={setPicked} pending={cand.isPending} />
      <p className="hint">De leverancier van het contract wordt de vaste uitvoerder van wat je toevoegt.</p>
    </Modal>
  );
}

export function ObligationChecklist({
  rows,
  picked,
  setPicked,
  pending,
}: {
  rows: UncoveredObligationDto[];
  picked: Set<string>;
  setPicked: (f: (p: Set<string>) => Set<string>) => void;
  pending?: boolean;
}) {
  if (pending) return <Loading />;
  if (!rows.length) return <p className="muted small">Geen uitvoeringen zonder contract in deze gebouwen.</p>;
  const all = rows.every((r) => picked.has(r.id));
  return (
    <fieldset className="checklist">
      <legend className="sr-only">Uitvoeringen</legend>
      <label>
        <input
          type="checkbox"
          checked={all}
          onChange={() => setPicked(() => (all ? new Set() : new Set(rows.map((r) => r.id))))}
        />
        <span>Alles kiezen ({rows.length})</span>
      </label>
      {rows.map((r) => (
        <label key={r.id}>
          <input
            type="checkbox"
            checked={picked.has(r.id)}
            onChange={() =>
              setPicked((p) => {
                const n = new Set(p);
                if (n.has(r.id)) n.delete(r.id);
                else n.add(r.id);
                return n;
              })
            }
          />
          <span>
            {r.name}
            <span className="small muted">
              {" "}
              <Meta
                items={[
                  r.asset ? `${r.asset.code} ${r.asset.name}`.trim() : "Bij het gebouw",
                  OBLIGATION_KIND_LABELS[r.kind as ObligationKind] ?? r.kind,
                  intervalText(r),
                  r.building.code,
                  r.nextDueOn && `volgende ${fmtDate(r.nextDueOn)}`,
                ]}
              />
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** Assets kiezen waarvan de storingen onder een storingspost vallen (afkoop). */
export function AssetCoverDialog({
  contract: c,
  line: l,
  onClose,
}: {
  contract: ContractDetailDto;
  line: ContractLineDto;
  onClose: () => void;
}) {
  const toast = useToast();
  const refresh = useContractsRefresh();
  const buildingList = l.building ? [l.building] : c.buildings;
  const qs = useQueries({
    queries: buildingList.map((b) => ({
      queryKey: buildingKey(b.id),
      queryFn: () => api<BuildingDto>(`/buildings/${b.id}`),
    })),
  });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const have = new Set(l.assets.map((a) => a.id));
  const norm = q.trim().toLocaleLowerCase("nl");
  const rows = qs
    .flatMap((x, i) => (x.data?.assets ?? []).map((a) => ({ ...a, building: buildingList[i]! })))
    .filter((a) => !have.has(a.id))
    .filter((a) => !norm || `${a.code} ${a.name}`.toLocaleLowerCase("nl").includes(norm))
    .sort((a, b) => a.building.code.localeCompare(b.building.code) || a.name.localeCompare(b.name, "nl"));
  const pending = qs.some((x) => x.isPending);
  const all = rows.length > 0 && rows.every((r) => picked.has(r.id));

  async function save() {
    setBusy(true);
    try {
      const d = await api<ContractDetailDto>(`/contract-lines/${l.id}/assets`, {
        method: "POST",
        body: { add: [...picked] },
      });
      await refresh(d);
      toast(`${picked.size === 1 ? "1 asset" : `${picked.size} assets`} onder de afkoop gebracht`);
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Assets toevoegen"
      onClose={onClose}
      busy={busy}
      wide
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void save()} disabled={busy || !picked.size}>
            {picked.size ? `${picked.size} toevoegen` : "Toevoegen"}
          </button>
        </>
      }
    >
      <p className="muted small">
        Storingen aan deze assets vallen onder <strong>{l.description}</strong>. Een melding in de Inbox laat dat zien,
        met het storingsnummer van de leverancier.
      </p>
      <label className="field">
        <span>Zoeken</span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code of naam" />
      </label>
      {pending ? (
        <Loading />
      ) : !rows.length ? (
        <p className="muted small">Geen assets meer om toe te voegen.</p>
      ) : (
        <fieldset className="checklist">
          <legend className="sr-only">Assets</legend>
          <label>
            <input
              type="checkbox"
              checked={all}
              onChange={() => setPicked(all ? new Set() : new Set(rows.map((r) => r.id)))}
            />
            <span>Alles kiezen ({rows.length})</span>
          </label>
          {rows.map((a) => (
            <label key={a.id}>
              <input
                type="checkbox"
                checked={picked.has(a.id)}
                onChange={() =>
                  setPicked((p) => {
                    const n = new Set(p);
                    if (n.has(a.id)) n.delete(a.id);
                    else n.add(a.id);
                    return n;
                  })
                }
              />
              <span>
                {a.name}
                <span className="small muted"> {[a.code, a.building.code].filter(Boolean).join(" · ")}</span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
    </Modal>
  );
}
